// Reusable flyout mechanics shared by the standalone `aquifer-ui-flyout`
// controller and the sidebar controller.
//
// A Flyout manages a single trigger -> panel pair:
//   - open / close / toggle (keeps aria-expanded + [hidden] in sync)
//   - places the panel beside/below the trigger when detached
//   - optionally detaches the panel into a positioning root and restores it
//   - dismisses on outside mousedown, ancestor scroll, and Escape
//
// Class names are configurable so callers (e.g. the sidebar) can reuse their
// own styling hooks. A shared registry enforces a single open flyout at a time.

const openFlyouts = new Set()

const DEFAULT_OPEN_CLASS = "is-open"
const DEFAULT_DETACHED_CLASS = "aqui-flyout--detached"

export class Flyout {
  static closeAll({ except } = {}) {
    openFlyouts.forEach((flyout) => {
      if (flyout !== except) flyout.close()
    })
  }

  constructor({
    trigger,
    panel,
    container = null,
    root = null,
    placement = "bottom-start",
    detach = false,
    gap = 8,
    openClass = DEFAULT_OPEN_CLASS,
    detachedClass = DEFAULT_DETACHED_CLASS,
    closeOnEscape = true,
    closeOnScroll = true,
    onOpen = null,
    onClose = null,
  }) {
    this.trigger = trigger
    this.panel = panel
    this.container = container || trigger?.parentElement || trigger
    this.root = root
    this.placement = placement
    this.detach = detach
    this.gap = gap
    this.openClass = openClass
    this.detachedClass = detachedClass
    this.closeOnEscape = closeOnEscape
    this.closeOnScroll = closeOnScroll
    this.onOpen = onOpen
    this.onClose = onClose
    this.origin = null

    this.handleOutside = this.handleOutside.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
  }

  get isOpen() {
    return this.container.classList.contains(this.openClass)
  }

  toggle() {
    if (this.isOpen) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (this.isOpen) return

    Flyout.closeAll()

    this.trigger?.setAttribute("aria-expanded", "true")
    this.panel.hidden = false
    this.container.classList.add(this.openClass)

    if (this.detach) this.detachPanel()

    this.bindDismiss()
    openFlyouts.add(this)
    this.onOpen?.(this)
  }

  close() {
    if (!this.isOpen) return

    this.container.classList.remove(this.openClass)
    this.trigger?.setAttribute("aria-expanded", "false")
    this.panel.hidden = true

    if (this.detach) this.restorePanel()

    this.unbindDismiss()
    openFlyouts.delete(this)
    this.onClose?.(this)
  }

  destroy() {
    this.close()
    openFlyouts.delete(this)
  }

  // --- Detach / restore ------------------------------------------------------
  detachPanel() {
    const root = this.root
    if (!root) return

    if (!this.origin) {
      this.origin = {
        parent: this.panel.parentElement,
        nextSibling: this.panel.nextElementSibling,
      }
    }

    root.appendChild(this.panel)
    this.panel.classList.add(this.detachedClass)
    this.positionPanel()
  }

  restorePanel() {
    this.panel.classList.remove(this.detachedClass)
    this.panel.style.position = ""
    this.panel.style.top = ""
    this.panel.style.left = ""

    const origin = this.origin
    if (!origin?.parent) return

    if (origin.nextSibling) {
      origin.parent.insertBefore(this.panel, origin.nextSibling)
    } else {
      origin.parent.appendChild(this.panel)
    }

    this.origin = null
  }

  positionPanel() {
    const root = this.root
    if (!root) return

    const rootRect = root.getBoundingClientRect()
    const triggerRect = this.trigger.getBoundingClientRect()

    this.panel.style.position = "absolute"

    const top = triggerRect.top - rootRect.top + root.scrollTop
    const bottom = triggerRect.bottom - rootRect.top + root.scrollTop
    const left = triggerRect.left - rootRect.left
    const right = triggerRect.right - rootRect.left

    switch (this.placement) {
      case "right-start":
        this.panel.style.top = `${top}px`
        this.panel.style.left = `${right + this.gap}px`
        break
      case "left-start":
        this.panel.style.top = `${top}px`
        this.panel.style.left = `${left - this.panel.offsetWidth - this.gap}px`
        break
      case "top-start":
        this.panel.style.top = `${top - this.panel.offsetHeight - this.gap}px`
        this.panel.style.left = `${left}px`
        break
      default:
        // bottom-start (default)
        this.panel.style.top = `${bottom + this.gap}px`
        this.panel.style.left = `${left}px`
    }
  }

  // --- Dismissal -------------------------------------------------------------
  bindDismiss() {
    document.addEventListener("mousedown", this.handleOutside)
    if (this.closeOnScroll) {
      document.addEventListener("scroll", this.handleScroll, { capture: true, passive: true })
    }
    if (this.closeOnEscape) {
      document.addEventListener("keydown", this.handleKeydown)
    }
  }

  unbindDismiss() {
    document.removeEventListener("mousedown", this.handleOutside)
    if (this.closeOnScroll) {
      document.removeEventListener("scroll", this.handleScroll, { capture: true })
    }
    if (this.closeOnEscape) {
      document.removeEventListener("keydown", this.handleKeydown)
    }
  }

  handleOutside(event) {
    if (this.container.contains(event.target)) return
    if (this.trigger?.contains(event.target)) return
    if (this.panel.contains(event.target)) return

    this.close()
  }

  handleScroll(event) {
    // Ignore scrolling inside the panel itself; close when the surrounding
    // UI (menu, page, etc.) scrolls out from under the flyout.
    if (this.panel.contains(event.target)) return

    this.close()
  }

  handleKeydown(event) {
    if (event.key !== "Escape") return

    event.preventDefault()
    this.close()
  }
}
